/* eslint-disable no-param-reassign */
import Vue from 'vue'
import {
  ref, reactive, computed, watch, onUnmounted,
} from '@vue/composition-api'
import { parseISO, format } from 'date-fns'
import AggregationApi from '@/api/v2/eod-closing/Aggregation'
import AggregationUserApi from '@/api/v2/eod-closing/AggregationUser'
import BusinessRuleApi from '@/api/v2/eod-closing/BusinessRule'
import DataIntegrityDiagnosticApi from '@/api/v2/eod-closing/DataIntegrityDiagnostic'
import useAggregation from '@/views/v2-temp/eod-closing/composables/useAggregation'
import useGlobalBulkCompletion from '@/composables/useGlobalBulkCompletion'

/**
 * 新クロージングページ（日報β専用）の日計集計ロジック。
 *
 * 既存 `eod-closing/components/Aggregations.vue` のAPI呼び出し
 * （`getAggregations` / `enqueueAggregation` / `enqueueGenerateBusinessRuleResults`
 *  / `getAggregationJobStatus` ポーリング）をそのまま流用する。
 * 既存 `eod-closing` 配下は変更せず、ロジックのみをこの composable に再構成している。
 *
 * @param {import('@vue/composition-api').Ref<string>} dateRef - 営業日 (YYYY-MM-DD)
 * @param {Object} deps - 依存オブジェクト
 * @param {Function} deps.handleApiError - APIエラーハンドラ
 */
export default function useEodClosingV2Aggregations(dateRef, { handleApiError }) {
  const isLoading = ref(true)
  const aggregations = ref([])
  const aggregationsData = ref([])
  const loadingStatuses = reactive({})
  const jobTimestamps = reactive({})
  const jobErrorMessages = reactive({})
  const jobStatuses = ref({})
  // 集計表ID -> 適用候補ユーザー一覧（適用ユーザーの変更ダイアログ用）
  const availableUsers = ref({})

  // 集計表ID -> 「集計後にタイムカードが変更されている → 要再集計」フラグ。
  // データ整合性診断 API の category=timecard_updated_after_job を集計表単位に
  // 集約したもの。タイムカード編集 → 戻ってきたとき、シート上に「要再集計」を
  // 出すために使う。
  const needsReaggregationByAggregationId = ref({})

  // 当日出勤しているのに、どのユーザー集計表の対象にもなっていないユーザ一覧
  // （診断 API の category=attended_user_not_in_aggregation）。
  // ユーザー集計ステップに警告と「集計表に登録」導線を出すために使う。
  const unassignedAttendees = ref([])

  // bulk_complete の状態はグローバル singleton（画面遷移をまたいで保持）から取る。
  // 通知トーストも同じ singleton を購読する。
  const globalBulk = useGlobalBulkCompletion()
  const bulkCompletionByType = globalBulk.runningByType

  const {
    aggregationResultsMap,
    updateAggregationResult,
    sortAndFilterAggregationType,
    buildAggregationMap,
  } = useAggregation(aggregations)

  // 集計表タイプごとのデータ（position 順）
  const userAggregationsData = computed(() => sortAndFilterAggregationType(aggregationsData.value, 'EodClosingUserAggregation'))
  const tableAggregationsData = computed(() => sortAndFilterAggregationType(aggregationsData.value, 'EodClosingTableAggregation'))
  const clubAggregationsData = computed(() => sortAndFilterAggregationType(aggregationsData.value, 'EodClosingClubAggregation'))
  const userAggregationsMap = computed(() => buildAggregationMap(sortAndFilterAggregationType(aggregations.value, 'EodClosingUserAggregation')))
  const tableAggregationsMap = computed(() => buildAggregationMap(sortAndFilterAggregationType(aggregations.value, 'EodClosingTableAggregation')))
  const clubAggregationsMap = computed(() => buildAggregationMap(sortAndFilterAggregationType(aggregations.value, 'EodClosingClubAggregation')))

  const aggregationUsers = id => {
    const agg = aggregationsData.value.find(ag => ag.attributes.id === Number(id))
    if (agg?.attributes?.type === 'EodClosingUserAggregation') {
      return agg.attributes.aggregationUsers.data
    }

    return []
  }

  const showFormattedTimestamp = timestampStr => {
    if (!timestampStr) return ''

    return format(parseISO(timestampStr), 'yyyy/MM/dd HH:mm')
  }

  /**
   * 集計表の「シート状態」を判定する。
   *  - error              : ジョブ失敗
   *  - progress           : 集計中
   *  - uncounted          : 集計ジョブ未実行（未集計）
   *  - unsaved            : 集計済みだが未保存の結果がある
   *  - needs_reaggregation: 保存済みだが集計後にタイムカードが変更された
   *  - saved              : すべて保存済み（または対象データなし）
   *
   * 優先度: error > progress > uncounted > unsaved > needs_reaggregation > saved
   * （未保存セルがある場合は、まずユーザーに保存させてから再集計を促す）
   */
  const sheetStatus = id => {
    const status = jobStatuses.value[id]
    if (status === 'failed' || status === 'lambda_failed') return 'error'
    if (status === 'in_progress') return 'progress'
    if (status === null || status === undefined) return 'uncounted'

    // 未保存判定: 結果に businessRuleResultId が無い（= isDirty / 未保存）セルがあるか
    const agg = aggregations.value.find(ag => ag?.attributes?.id === Number(id))
    if (agg?.attributes?.contexts?.data) {
      const hasUnsaved = agg.attributes.contexts.data.some(context => (
        (context.attributes?.results?.data || []).some(result => {
          const attrs = result.attributes || {}

          // 給与サマリーの計算列（PayrollSummaryColumn: businessRuleId が 'payroll_' 始まりの文字列）は
          // Earning から自動算出する表示専用列で保存対象ではないため、未保存判定から除外する。
          if (typeof attrs.businessRuleId === 'string' && attrs.businessRuleId.startsWith('payroll_')) return false

          return attrs.value !== null
            && attrs.value !== undefined
            && !attrs.businessRuleResultId
        })
      ))
      if (hasUnsaved) return 'unsaved'
    }

    // タイムカードが集計後に更新された場合は「要再集計」を返す。
    if (needsReaggregationByAggregationId.value[id]) return 'needs_reaggregation'

    return 'saved'
  }

  // いずれかの集計表が集計中か（グローバル集計中インジケータ用）。
  //  - loadingStatuses: セル保存後の依存関係再計算 API 実行中
  //  - jobStatuses === 'in_progress': 集計ジョブ実行中
  const isAnyAggregating = computed(() => (
    Object.values(loadingStatuses).some(Boolean)
    || Object.values(jobStatuses.value).some(status => status === 'in_progress')
  ))

  const upsertAggregation = data => {
    if (!data || !data.attributes) return

    const idx = aggregations.value.findIndex(ag => ag.attributes.id === data.attributes.id)
    if (idx >= 0) {
      aggregations.value.splice(idx, 1, data)
    } else {
      aggregations.value.push(data)
    }
  }

  const getAggregationJobStatus = async id => {
    try {
      // 日報ベータの集計表では給与サマリー計算列（総支給額・総控除額・差引支給額・残り支給額・源泉）を表示する
      const res = await AggregationApi.getAggregationJobStatus(id, dateRef.value, { withPayrollSummary: true })
      if (res.data.status === 'success') {
        const {
          status, result, meta, errorMessage,
        } = res.data.data
        jobStatuses.value = { ...jobStatuses.value, [id]: status }
        jobErrorMessages[id] = errorMessage
        jobTimestamps[id] = { createdAt: meta.createdAt, updatedAt: meta.updatedAt }
        if (result && result.data) upsertAggregation(result.data)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        jobStatuses.value = { ...jobStatuses.value, [id]: null }
      } else {
        handleApiError(error)
      }
    }
  }

  /**
   * データ整合性診断 API（当日分）を取得し、集計表単位の「要再集計」フラグを更新する。
   *
   * 期間は当日 1 日分のみ。レスポンスの finding を走査し、「要再集計」系の
   * category を集計表 ID 単位に集約する。
   *  - timecard_updated_after_job: 集計後に元データ（タイムカード等）が変更された
   *  - balance_receivable_payment_updated_after_job: 集計後に売掛の回収が登録された
   *    （回収は締め作業のあとに登録されることが多く、この順序は普通に起きる）
   *  - dependent_aggregation_updated_after_job: 依存先（上流）集計表の保存後に
   *    この集計が更新されていない（例: ユーザー集計の再保存後の店舗集計）
   *  - receipt_updated_after_job: 集計後に伝票まわり（卓・注文明細・受領者・会計・
   *    卓タグ・紹介・支払・売掛の発生）が変更された
   *  - expense_updated_after_job: 集計後に経費・諸入費が変更された（金額・ラベル・税率・
   *    現金/非現金の切り替え・削除）。経費の定数を参照する店舗集計だけが対象
   * 診断が取れない／エラーのときはフラグを空にする（黙ってフェールオープン）。
   */
  const NEEDS_REAGGREGATION_CATEGORIES = [
    'timecard_updated_after_job',
    'balance_receivable_payment_updated_after_job',
    'dependent_aggregation_updated_after_job',
    'receipt_updated_after_job',
    'expense_updated_after_job',
  ]

  const fetchDailyDiagnostic = async () => {
    if (!dateRef.value) return
    try {
      const res = await DataIntegrityDiagnosticApi.getDiagnostics(dateRef.value, dateRef.value)
      const payload = res.data?.data ?? res.data ?? {}
      const rows = payload.rows || []
      const map = {}
      const attendees = {}
      rows.forEach(row => {
        const findings = row.findings || []
        findings.forEach(f => {
          if (f.category === 'attended_user_not_in_aggregation') {
            if (f.context_id != null) attendees[f.context_id] = { id: f.context_id, name: f.context_name }

            return
          }
          if (!NEEDS_REAGGREGATION_CATEGORIES.includes(f.category)) return
          if (f.aggregation_id == null) return
          map[f.aggregation_id] = true
        })
      })
      needsReaggregationByAggregationId.value = map
      unassignedAttendees.value = Object.values(attendees)
    } catch (error) {
      // 診断 API が失敗してもクロージング画面の動作は阻害しない。
      needsReaggregationByAggregationId.value = {}
      unassignedAttendees.value = []
    }
  }

  // --- ポーリングの後始末（リーク防止）-----------------------------------
  // 発行したタイマー ID と visibilitychange リスナーを集約し、画面遷移（unmount）時に
  // 全てクリアする。破棄済み画面の状態を更新し続ける孤立ポーリングを防ぐ。
  const activeTimerIds = new Set()
  const activeVisibilityListeners = new Set()

  const registerVisibilityListener = listener => {
    document.addEventListener('visibilitychange', listener)
    activeVisibilityListeners.add(listener)
  }

  const unregisterVisibilityListener = listener => {
    document.removeEventListener('visibilitychange', listener)
    activeVisibilityListeners.delete(listener)
  }

  onUnmounted(() => {
    activeTimerIds.forEach(timerId => {
      clearTimeout(timerId)
      clearInterval(timerId)
    })
    activeTimerIds.clear()
    activeVisibilityListeners.forEach(listener => document.removeEventListener('visibilitychange', listener))
    activeVisibilityListeners.clear()
  })

  // ジョブ状態のポーリング。setTimeout の自己再スケジュールで 5秒→10秒→…→最大60秒と
  // 間隔を広げる（指数バックオフ）。長時間ジョブにサーバーへ無駄打ちしないため。
  // ※ 旧実装は setInterval で初回の 5 秒しか反映されず、バックオフがデッドコードだった。
  const POLL_BASE_INTERVAL_MS = 5000
  const POLL_MAX_INTERVAL_MS = 60000

  const pollAggregationJobStatus = id => {
    let intervalTime = POLL_BASE_INTERVAL_MS

    const schedule = () => {
      const timerId = setTimeout(async () => {
        activeTimerIds.delete(timerId)
        try {
          await getAggregationJobStatus(id)
          if (['completed', 'failed', null].includes(jobStatuses.value[id])) {
            // 再集計や一括保存ジョブの完了直後は、タイムカード変更が解消された
            // 可能性があるため、診断を取り直して「要再集計」表示を更新する。
            fetchDailyDiagnostic()

            return
          }
        } catch (error) {
          if (error.response?.status !== 404) {
            handleApiError(error)
          }

          return
        }
        intervalTime = Math.min(intervalTime * 2, POLL_MAX_INTERVAL_MS)
        schedule()
      }, intervalTime)
      activeTimerIds.add(timerId)
    }

    schedule()
  }

  const enqueueAggregation = async (id, options = {}) => {
    const { forceReload = false, forceRefresh = false } = options
    try {
      const res = await AggregationApi.enqueueAggregation(id, dateRef.value, { forceReload, forceRefresh })
      if (res.status === 202) {
        jobStatuses.value = { ...jobStatuses.value, [id]: 'in_progress' }
        pollAggregationJobStatus(id)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        jobStatuses.value = { ...jobStatuses.value, [id]: null }
      } else {
        handleApiError(error)
      }
    }
  }

  const getAggregations = async () => {
    isLoading.value = true
    try {
      const res = await AggregationApi.getAggregations(dateRef.value)
      if (res.data.status === 'success') {
        const { data } = res.data.data
        aggregationsData.value = data
        const aggregationIds = []
        data.forEach(ag => {
          Vue.set(loadingStatuses, ag.attributes.id, false)
          Vue.set(jobTimestamps, ag.attributes.id, { createdAt: null, updatedAt: null })
          Vue.set(jobErrorMessages, ag.attributes.id, null)
          jobStatuses.value = { ...jobStatuses.value, [ag.attributes.id]: null }
          availableUsers.value = { ...availableUsers.value, [ag.attributes.id]: [] }
          aggregationIds.push(ag.attributes.id)
        })
        await Promise.all(aggregationIds.map(id => getAggregationJobStatus(id)))
        // 集計データのロード完了後、診断（要再集計フラグ）も取り直す。
        fetchDailyDiagnostic()
        // 実行中（in_progress）のジョブがあれば追跡を再開する。
        // 集計中に画面遷移・リロードすると元のポーリングは破棄されるため、
        // ここで再開しないと完了後も「集計中」表示のまま取り残される。
        aggregationIds
          .filter(id => jobStatuses.value[id] === 'in_progress')
          .forEach(id => pollAggregationJobStatus(id))
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      isLoading.value = false
    }
  }

  // 保存結果以外を再計算（再集計）
  const reloadAggregation = id => {
    enqueueAggregation(id, { forceReload: true })
  }

  // 保存結果を削除してすべてを再計算（リセットして再集計）
  const refreshAggregation = id => {
    enqueueAggregation(id, { forceReload: true, forceRefresh: true })
  }

  // カテゴリ単位の「日計ジョブ一括実行」。
  // 既存 `eod-closing/components/Aggregations.vue` の enqueueAggregations を参考に、
  // 指定タイプの集計表のうち未実行（null）または失敗（failed）のものをまとめて
  // enqueue する。集計中（in_progress）/完了済み（completed）はスキップ。
  const enqueueAggregationsByType = async type => {
    const data = {
      EodClosingUserAggregation: userAggregationsData.value,
      EodClosingTableAggregation: tableAggregationsData.value,
      EodClosingClubAggregation: clubAggregationsData.value,
    }[type]
    if (!data) return

    const promises = data
      .filter(agg => {
        const status = jobStatuses.value[agg.attributes.id]

        return status === null || status === undefined || status === 'failed'
      })
      .map(agg => enqueueAggregation(agg.attributes.id))
    await Promise.all(promises)
  }

  // カテゴリ単位の集計表データを返すヘルパー
  const aggregationsDataForType = type => ({
    EodClosingUserAggregation: userAggregationsData.value,
    EodClosingTableAggregation: tableAggregationsData.value,
    EodClosingClubAggregation: clubAggregationsData.value,
  }[type] || [])

  // bulk_complete 実行中か（カテゴリ単位）。
  const isBulkCompleting = type => bulkCompletionByType[type]?.status === 'in_progress'

  // 店舗集計（Club）を起動する前に、上流（ユーザー集計・卓集計）の保存状態を確認する。
  // 2026-06-02: サーバ側のクロスタイプ待ち合わせ装置（D-3）撤廃に伴い、フロント側で
  // 上流が saved / progress / 未保存 のどれかを判定し、ユーザーに案内する。
  // 戻り値:
  //   - { state: 'ready' }                    : そのまま店舗集計を起動してよい
  //   - { state: 'in_progress' }              : 上流が実行中。完了待ちを案内する
  //   - { state: 'not_ready' }                : 上流が未保存・未集計。先に実行を案内する
  const upstreamReadinessForBulkComplete = downstreamType => {
    // ユーザー集計・卓集計は上流なし
    if (downstreamType !== 'EodClosingClubAggregation') return { state: 'ready' }

    const upstreamAggs = [
      ...userAggregationsData.value,
      ...tableAggregationsData.value,
    ]
    if (upstreamAggs.length === 0) return { state: 'ready' }

    const upstreamStates = upstreamAggs.map(agg => sheetStatus(agg.attributes.id))
    if (upstreamStates.some(s => s === 'progress')) return { state: 'in_progress' }
    if (upstreamStates.some(s => s !== 'saved')) return { state: 'not_ready' }

    return { state: 'ready' }
  }

  // 当該タイプの集計表の job ステータス・結果を取り直す（bulk_complete の進捗反映用）。
  const refreshAggregationStatusesForType = async type => {
    await Promise.all(
      aggregationsDataForType(type).map(agg => getAggregationJobStatus(agg.attributes.id)),
    )
  }

  // 「完了する」の実行対象（未完了の集計表 ID）。
  // saved（完了済み）と progress（集計中）は除外。
  // unsaved / uncounted / error / needs_reaggregation（要再集計）は対象。
  const pendingAggregationIdsForType = type => (
    aggregationsDataForType(type)
      .map(agg => agg.attributes.id)
      .filter(id => {
        const s = sheetStatus(id)

        return s !== 'saved' && s !== 'progress'
      })
  )

  // カテゴリ配下の「未完了」集計表だけを依存順に「リセット→再集計→保存」する。
  // 完了済み（saved）はスキップする。対象が 0 件なら何もしない（ボタンも無効化される）。
  // 状態の永続化・ポーリングはグローバル singleton（useGlobalBulkCompletion）が担う。
  const bulkCompleteByType = async type => {
    if (isBulkCompleting(type)) return
    const aggregationIds = pendingAggregationIdsForType(type)
    if (aggregationIds.length === 0) return
    await globalBulk.startBulkComplete({
      type,
      date: dateRef.value,
      aggregationIds,
      onError: handleApiError,
    })
  }

  // カテゴリ配下の「全集計表」を依存順に reset→集計→保存する（完了済みも含めて全実行）。
  // ⋮「完了済みも含めて全て実行」用。aggregationIds を渡さず全表示対象を実行する。
  const bulkCompleteAllByType = async type => {
    if (isBulkCompleting(type)) return
    await globalBulk.startBulkComplete({
      type,
      date: dateRef.value,
      onError: handleApiError,
    })
  }

  // --- 集計表単位の個別集計（リセット→集計→保存）------------------------
  // 「完了する」と同じサーバ側 bulk_complete を aggregation_id 付きで起動し、
  // 集計表 1 つだけを対象に reset→集計→保存する。型単位の全面プログレスは
  // 出さず、当該シートだけ「集計中」表示にする（グローバル singleton には登録しない）。
  const individualCompletingIds = ref({})
  const isIndividualCompleting = id => !!individualCompletingIds.value[id]

  const INDIVIDUAL_POLL_INTERVAL_MS = 3000

  // 単一集計表の bulk_complete 完了をポーリングで待つ。
  // 裏タブではブラウザがタイマーを最大 1 分程度まで間引くため、タブが表に戻った
  // 瞬間にも 1 回即確認する（visibilitychange）。長時間の集計を裏タブで待って
  // 戻ったとき、リロードせずすぐ「保存済み」へ切り替わる。
  // 集計実行中はサーバーが重く一時的な通信エラーが起きやすい。1 回の失敗で
  // 諦めるとチップが「集計中」のまま取り残されるため、連続失敗の閾値まではリトライする
  // （グローバルトースト側のポーリングと同じ方針）。
  const INDIVIDUAL_POLL_FAILURE_LIMIT = 5

  const pollIndividualBulkComplete = jobId => new Promise(resolve => {
    let interval = null
    let failureCount = 0

    const cleanup = () => {
      clearInterval(interval)
      activeTimerIds.delete(interval)
      // eslint-disable-next-line no-use-before-define
      unregisterVisibilityListener(onVisible)
    }

    const check = async () => {
      try {
        const res = await AggregationApi.getBulkCompleteStatus(jobId)
        const payload = res.data?.data ?? res.data ?? {}
        failureCount = 0
        if (payload.status === 'completed') {
          cleanup()
          resolve(payload)
        }
      } catch (error) {
        failureCount += 1
        if (failureCount >= INDIVIDUAL_POLL_FAILURE_LIMIT) {
          cleanup()
          resolve(null)
        }
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }

    interval = setInterval(check, INDIVIDUAL_POLL_INTERVAL_MS)
    activeTimerIds.add(interval)
    registerVisibilityListener(onVisible)
  })

  // 完了直後の job 状態取得を、確定状態（completed/failed/未実行）になるまでリトライする。
  // 保存直後は DB が後始末（巨大ジョブへのパッチ等）で重く、1 回きりの取得だと失敗や
  // 古い値（in_progress）を掴んで、チップが「集計中」のまま取り残されることがあるため。
  const INDIVIDUAL_REFRESH_RETRIES = 5
  const INDIVIDUAL_REFRESH_DELAY_MS = 3000

  const refreshJobStatusUntilSettled = async aggregationId => {
    for (let i = 0; i < INDIVIDUAL_REFRESH_RETRIES; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await getAggregationJobStatus(aggregationId)
      const status = jobStatuses.value[aggregationId]
      if (status !== 'in_progress') return

      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => { setTimeout(resolve, INDIVIDUAL_REFRESH_DELAY_MS) })
    }
  }

  const bulkCompleteAggregation = async (aggregationId, type) => {
    if (isIndividualCompleting(aggregationId)) return
    individualCompletingIds.value = { ...individualCompletingIds.value, [aggregationId]: true }
    // 当該シートの状態チップを「集計中」にする（そのシートだけ集計中表示）。
    jobStatuses.value = { ...jobStatuses.value, [aggregationId]: 'in_progress' }
    try {
      const res = await AggregationApi.bulkComplete(type, dateRef.value, [aggregationId])
      if (res.status === 202) {
        const jobId = res.data?.jobId ?? res.data?.job_id
        await pollIndividualBulkComplete(jobId)
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      individualCompletingIds.value = { ...individualCompletingIds.value, [aggregationId]: false }
      // 集計表の最新 job 状態・結果と、要再集計診断を取り直す（確定までリトライ）。
      await refreshJobStatusUntilSettled(aggregationId)
      fetchDailyDiagnostic()
    }
  }

  // singleton の進捗が変わったときに、当該タイプのシート状態と診断を最新化する。
  // 画面に居る間だけ動く（unmount で watcher 破棄）。
  const lastSeenStatusByType = {}
  watch(
    () => ({
      EodClosingUserAggregation: bulkCompletionByType.EodClosingUserAggregation?.status,
      EodClosingTableAggregation: bulkCompletionByType.EodClosingTableAggregation?.status,
      EodClosingClubAggregation: bulkCompletionByType.EodClosingClubAggregation?.status,
    }),
    newStatuses => {
      Object.entries(newStatuses).forEach(([type, status]) => {
        if (lastSeenStatusByType[type] === status) return
        lastSeenStatusByType[type] = status
        if (status === 'in_progress' || status === 'completed') {
          refreshAggregationStatusesForType(type).catch(() => {})
        }
        if (status === 'completed') {
          fetchDailyDiagnostic()
        }
      })
    },
    { deep: true },
  )

  // 一括保存
  const enqueueGenerateBusinessRuleResults = async (id, options = {}) => {
    const { forceReload = false, forceRefresh = false } = options
    try {
      const res = await BusinessRuleApi.enqueueGenerateBusinessRuleResults(id, dateRef.value, { forceReload, forceRefresh })
      if (res.status === 202) {
        jobStatuses.value = { ...jobStatuses.value, [id]: 'in_progress' }
        pollAggregationJobStatus(id)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        jobStatuses.value = { ...jobStatuses.value, [id]: null }
      } else {
        handleApiError(error)
      }
    }
  }

  // 保存レスポンスの context と、集計データ側の context が同一かを判定する。
  // Club コンテキストは集計表に 1 つだけのため contextType 一致で同一とみなす
  // （Club の contextId は集計データ側とレスポンス側で持ち方が異なりうる）。
  // User / Table は contextId を null 許容で突き合わせる。
  const isSameContext = (ctxAttrs, contextType, contextId) => {
    if (ctxAttrs.contextType !== contextType) return false
    if (contextType === 'Club') return true

    return (ctxAttrs.id ?? null) === (contextId ?? null)
  }

  /**
   * 保存APIのレスポンス（BusinessRuleResult）を集計データへマージする。
   *
   * 既存 `updateAggregationResult` は「既に result 行があるセル」しか更新できず、
   * 空セル（result 行が存在しないセル）への初入力は反映できなかった。
   * ここでは、対象 context の results.data に該当行が無ければ
   * 取得時と同じ形（AggregationFetch::ResultSerializer 相当）の行を新規追加する。
   *
   * 保存レスポンスの attributes:
   *   { id, businessRuleId, resultValue, contextId, contextType, ... }
   * 取得時の result.attributes:
   *   { id, businessRuleId, businessRuleResultId, value, editable, ... }
   *
   * @param {Object} businessRuleResult - 保存APIが返した BusinessRuleResult
   */
  const mergeBusinessRuleResult = businessRuleResult => {
    const attrs = businessRuleResult?.attributes
    if (!attrs) return

    const {
      id: businessRuleResultId,
      businessRuleId,
      resultValue,
      contextType,
      contextId,
      isDirty,
    } = attrs

    // まず既存行の更新を試みる（依存ルールの再計算結果なども含めて反映）。
    updateAggregationResult(businessRuleResult)

    // 既存行が無い場合に備え、対象 context の results.data へ新規行を追加する。
    aggregations.value.forEach(aggregation => {
      const contexts = aggregation?.attributes?.contexts?.data
      if (!Array.isArray(contexts)) return

      contexts.forEach(context => {
        const ctxAttrs = context?.attributes
        if (!ctxAttrs) return
        if (!isSameContext(ctxAttrs, contextType, contextId)) return

        if (!ctxAttrs.results) Vue.set(ctxAttrs, 'results', { data: [] })
        if (!Array.isArray(ctxAttrs.results.data)) Vue.set(ctxAttrs.results, 'data', [])

        const resultsData = ctxAttrs.results.data
        const existing = resultsData.find(r => r?.attributes?.businessRuleId === businessRuleId)

        if (existing) {
          // 既存行は updateAggregationResult で更新済み。
          // 念のため value / businessRuleResultId を確定値で揃える。
          Vue.set(existing.attributes, 'value', resultValue)
          Vue.set(existing.attributes, 'businessRuleResultId', businessRuleResultId)
          // 手修正フラグもサーバの確定値で揃える。ここを落とすと、保存直後に
          // グリッドが楽観的フラグを破棄した瞬間に手修正セルの色とバナーが消え、
          // 個別集計（＝集計データの再取得）まで戻らない。
          Vue.set(existing.attributes, 'isDirty', !!isDirty)

          return
        }

        // 新規行を取得時と同じ形で追加する。
        resultsData.push({
          id: `new-${businessRuleResultId}`,
          type: 'result',
          attributes: {
            id: `new-${businessRuleResultId}`,
            businessRuleId,
            businessRuleResultId,
            value: resultValue,
            dependentResources: [],
            dependentBusinessRuleIds: [],
            editable: true,
            isDirty: !!isDirty,
          },
        })
      })
    })
  }

  // セル単位保存
  // 実行中の保存リクエスト数。loadingStatuses は全集計表で共有のフラグなので、
  // 保存が並行したとき先に終わった側が false に戻さないよう、最後の 1 件で戻す。
  let savingCount = 0
  const generateBusinessRuleResult = async ({
    aggregationId,
    contextType,
    contextId,
    businessRuleId,
    resultValue,
  }) => {
    if (contextType !== 'Club' && !contextId) throw new TypeError('You need to specify contextId')

    savingCount += 1
    Object.keys(loadingStatuses).forEach(id => { loadingStatuses[id] = true })
    try {
      const res = await BusinessRuleApi.generateBusinessRuleResult({
        aggregationId,
        contextType,
        contextId,
        businessRuleId,
        resultValue,
        date: dateRef.value,
        // 新クロージングでは、再計算された下流のセルもその場で保存する（オプトイン）。
        // 旧クロージングはこのフラグを送らないため従来どおり（下流は未保存のまま）。
        options: { forceReload: false, persistDependents: true },
      })
      if (res.data.status === 'success') {
        // 既存行の更新だけでなく、空セル（result 行なし）への初入力も
        // 新規行追加で反映する。
        res.data.data.data.forEach(businessRuleResult => mergeBusinessRuleResult(businessRuleResult))
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      savingCount -= 1
      if (savingCount === 0) Object.keys(loadingStatuses).forEach(id => { loadingStatuses[id] = false })
    }
  }

  // --- 適用ユーザーの変更 ----------------------------------------------
  // 既存 `eod-closing/components/Aggregations.vue` の
  // createEodClosingUserAggregation / destroyEodClosingUserAggregation /
  // handleFetchAvailableUsers を参考に、新クロージングページ側に再構成したもの。

  // 適用候補ユーザー一覧を取得する
  const fetchAvailableUsers = async (id, callback = () => {}) => {
    try {
      const res = await AggregationApi.getAvailableUsers(id)
      if (res.data.status === 'success') {
        const { data } = res.data.data
        availableUsers.value = { ...availableUsers.value, [id]: data }
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      callback()
    }
  }

  // 集計表に適用ユーザーを追加する
  const createUserAggregation = async (
    id,
    { userId, callback } = { userId: null, callback: () => {} },
  ) => {
    try {
      const res = await AggregationUserApi.createAggregationUser(id, userId)
      if (res.data.status === 'success') {
        const { data } = res.data.data
        const aggIdx = aggregationsData.value.findIndex(ag => ag.attributes.id === Number(id))
        if (aggIdx < 0) return

        const updatedAggData = { ...aggregationsData.value[aggIdx] }
        updatedAggData.attributes.aggregationUsers.data.push(data)
        aggregationsData.value.splice(aggIdx, 1, updatedAggData)

        const updatedAvailableUsers = availableUsers.value[id] ? [...availableUsers.value[id]] : []
        const userIdx = updatedAvailableUsers.findIndex(user => user.attributes.id === Number(userId))
        if (userIdx < 0) return

        updatedAvailableUsers.splice(userIdx, 1)
        availableUsers.value = { ...availableUsers.value, [id]: updatedAvailableUsers }
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      callback()
    }
  }

  // 集計表から適用ユーザーを削除する
  const destroyUserAggregation = async (
    aggregationId,
    { id, userId, callback } = { id: null, userId: null, callback: () => {} },
  ) => {
    try {
      const res = await AggregationUserApi.destroyAggregationUser(id)
      if (res.data.status === 'success') {
        const { data } = res.data.data

        const aggIdx = aggregationsData.value.findIndex(ag => ag.attributes.id === Number(aggregationId))
        if (aggIdx < 0) return

        const updatedAggData = { ...aggregationsData.value[aggIdx] }
        const aggUserIdx = updatedAggData.attributes.aggregationUsers.data.findIndex(
          aggUser => aggUser.attributes.id === Number(id),
        )
        if (aggUserIdx < 0) return

        updatedAggData.attributes.aggregationUsers.data.splice(aggUserIdx, 1)
        aggregationsData.value.splice(aggIdx, 1, updatedAggData)

        // 解除されたユーザー（API が User として返す）を適用候補一覧へ戻す。
        // availableUsers のキーは他の箇所と同じく「集計表ID」を使う
        // （以前は aggregation_user レコードIDをキーにしていて、一覧に戻らないバグだった）。
        const updatedAvailableUsers = availableUsers.value[aggregationId]
          ? [...availableUsers.value[aggregationId]]
          : []
        const alreadyListed = updatedAvailableUsers.some(user => user.attributes.id === Number(userId))
        if (!alreadyListed) updatedAvailableUsers.push(data)
        availableUsers.value = { ...availableUsers.value, [aggregationId]: updatedAvailableUsers }
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      callback()
    }
  }

  return {
    isLoading,
    aggregations,
    aggregationsData,
    loadingStatuses,
    jobTimestamps,
    jobErrorMessages,
    jobStatuses,
    availableUsers,
    aggregationResultsMap,
    needsReaggregationByAggregationId,
    unassignedAttendees,
    fetchDailyDiagnostic,

    userAggregationsData,
    tableAggregationsData,
    clubAggregationsData,
    userAggregationsMap,
    tableAggregationsMap,
    clubAggregationsMap,

    aggregationUsers,
    showFormattedTimestamp,
    sheetStatus,
    isAnyAggregating,

    getAggregations,
    reloadAggregation,
    refreshAggregation,
    enqueueAggregationsByType,
    bulkCompletionByType,
    isBulkCompleting,
    pendingAggregationIdsForType,
    bulkCompleteByType,
    bulkCompleteAllByType,
    individualCompletingIds,
    isIndividualCompleting,
    bulkCompleteAggregation,
    upstreamReadinessForBulkComplete,
    enqueueGenerateBusinessRuleResults,
    generateBusinessRuleResult,
    fetchAvailableUsers,
    createUserAggregation,
    destroyUserAggregation,
  }
}
